import {
    manageRouteCheckboxAssignments
} from '@containers/activity-router/routes/helpers/assignment/assignment-selected/route-assignments-selected';
import {
    toggleActivityGridCheckbox,
    toggleStudentGridCheckbox,
    toggleTopicGridCheckbox
} from '@containers/activity-router/routes/helpers/slice/checkbox/slice-grid-checkbox-helpers';
import { toggleGridCells } from '@containers/activity-router/routes/helpers/slice/grid/slice-grid-helpers';
import {
    ActivityId,
    IsGridCellVisible,
    StudentId,
    TopicId,
    ToggleRouteActivityGridCheckboxParams,
    ToggleRouteStudentGridCheckboxParams,
    ToggleRouteTopicGridCheckboxParams
} from '@containers/activity-router/routes/helpers/slice/slice.types';

type ToggleRouteVisibleGridCellsParams = Pick<ToggleRouteStudentGridCheckboxParams, 'state' | 'manageSelectedAssignments'> & {
    isCellIncluded: (cell: { studentKey: StudentId; topicKey: TopicId; activityKey: ActivityId }) => boolean
};

// A filter can hide some of a row or topic's cells, so the checkbox only changes the visible ones and
// decides from them: hidden selections are neither added nor removed.
const toggleRouteVisibleGridCells = ({
    state,
    manageSelectedAssignments,
    isCellIncluded
}: ToggleRouteVisibleGridCellsParams) => {
    const includedCells = Object.keys(state.gridCells).flatMap((studentKey) =>
        Object.keys(state.gridCells[studentKey]).flatMap((topicKey) =>
            Object.keys(state.gridCells[studentKey][topicKey])
                .map((activityKey) => ({
                    studentKey,
                    topicKey,
                    activityKey
                }))
                .filter(isCellIncluded)));
    // Matches the grid checkboxes, which show partial as checked: any visible selection means clear.
    const isChecked = !includedCells.some(({ studentKey, topicKey, activityKey }) =>
        state.gridCells[studentKey][topicKey][activityKey].isSelected);

    let {
        gridCells,
        gridCounters
    } = state;

    includedCells.forEach(({ studentKey, topicKey, activityKey }) => {
        if (gridCells[studentKey][topicKey][activityKey].isSelected !== isChecked) {
            ({
                gridCells,
                gridCounters
            } = toggleGridCells({
                gridCells,
                gridCounters,
                studentId: studentKey,
                topicId: topicKey,
                activityId: activityKey,
                isSelected: isChecked
            }));
        }
    });

    state.assignmentsSelected = manageRouteCheckboxAssignments({
        selectedAssignments: state.assignmentsSelected,
        gridCells,
        isChecked,
        checkAssignmentEligibility: isCellIncluded,
        manageSelectedAssignments
    });

    return {
        gridCells,
        gridCounters,
        assignmentsSelected: state.assignmentsSelected
    };
};

export const getIsCellInTopics = (topicKeys?: TopicId[]): IsGridCellVisible | undefined =>
    topicKeys && (({ topicKey }) => topicKeys.includes(topicKey));

export const toggleRouteStudentGridCheckbox = ({
    payload,
    state,
    manageSelectedAssignments,
    isCellVisible
}: ToggleRouteStudentGridCheckboxParams) => {
    if (isCellVisible) {
        const studentId = payload.student.userProfileId.toString();

        return toggleRouteVisibleGridCells({
            state,
            manageSelectedAssignments,
            isCellIncluded: ({ studentKey, topicKey, activityKey }) => studentKey === studentId && isCellVisible({
                topicKey,
                activityKey
            })
        });
    }

    const {
        student,
        isChecked
    } = payload;
    const {
        gridCells,
        gridCounters
    } = toggleStudentGridCheckbox({
        gridCells: state.gridCells,
        gridCounters: state.gridCounters,
        isChecked,
        studentId: student.userProfileId.toString()
    });

    state.assignmentsSelected = manageRouteCheckboxAssignments({
        selectedAssignments: state.assignmentsSelected,
        gridCells: state.gridCells,
        isChecked,
        checkAssignmentEligibility: ({ studentKey }) => studentKey === student.userProfileId.toString(),
        manageSelectedAssignments
    });

    return {
        gridCells,
        gridCounters,
        assignmentsSelected: state.assignmentsSelected
    };
};

export const toggleRouteTopicGridCheckbox = ({
    payload,
    state,
    manageSelectedAssignments,
    isCellVisible
}: ToggleRouteTopicGridCheckboxParams) => {
    if (isCellVisible) {
        return toggleRouteVisibleGridCells({
            state,
            manageSelectedAssignments,
            isCellIncluded: ({ topicKey, activityKey }) => topicKey === payload.topicId && isCellVisible({
                topicKey,
                activityKey
            })
        });
    }

    const {
        topicId,
        isChecked
    } = payload;
    const {
        gridCells,
        gridCounters
    } = toggleTopicGridCheckbox({
        gridCells: state.gridCells,
        gridCounters: state.gridCounters,
        ...payload
    });

    state.assignmentsSelected = manageRouteCheckboxAssignments({
        selectedAssignments: state.assignmentsSelected,
        gridCells: state.gridCells,
        isChecked,
        checkAssignmentEligibility: ({ topicKey }) => topicKey === topicId,
        manageSelectedAssignments
    });

    return {
        gridCells,
        gridCounters,
        assignmentsSelected: state.assignmentsSelected
    };
};

export const toggleRouteActivityGridCheckbox = ({
    payload,
    state,
    manageSelectedAssignments
}: ToggleRouteActivityGridCheckboxParams) => {
    const {
        activityId,
        isChecked
    } = payload;
    const {
        gridCells,
        gridCounters
    } = toggleActivityGridCheckbox({
        gridCells: state.gridCells,
        gridCounters: state.gridCounters,
        ...payload
    });

    state.assignmentsSelected = manageRouteCheckboxAssignments({
        selectedAssignments: state.assignmentsSelected,
        gridCells: state.gridCells,
        isChecked,
        checkAssignmentEligibility: ({ activityKey }) => activityKey === activityId,
        manageSelectedAssignments
    });

    return {
        gridCells,
        gridCounters,
        assignmentsSelected: state.assignmentsSelected
    };
};
